import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslatedRecommendation } from '@/hooks/useTranslatedContent';
import { formatGuestDate, weekdayName } from './guestDates';
import {
  MapPin,
  Star,
  Heart,
  Phone,
  ExternalLink,
  Navigation,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  Calendar,
  Clock,
  Waves,
  Baby,
  Dog,
  Accessibility,
  Route,
  Timer,
  Download,
  Lightbulb,
  ShowerHead,
  Toilet,
  Shirt,
  LifeBuoy,
  Martini,
  Utensils,
  BedDouble,
  Umbrella,
  ParkingSquare,
  type LucideIcon,
} from 'lucide-react';
import { MapEmbed } from '@/components/ui/MapEmbed';
import type { LocalRecommendation, RecommendationCategory, CategoryGroup } from '@/types/database';
import { getCategoryInfo, RECOMMENDATION_CATEGORIES, CATEGORY_GROUPS } from '@/hooks/useLocalRecommendations';
import { RecommendationIcon } from './recommendationIcons';

interface GuestExploreTabProps {
  recommendations: LocalRecommendation[];
  darkMode: boolean;
}

// Beach Facility Icons — lucide (one icon language, no emoji)
const BEACH_FACILITY_ICONS: Record<string, LucideIcon> = {
  showers: ShowerHead,
  toilets: Toilet,
  changing_rooms: Shirt,
  lifeguard: LifeBuoy,
  beach_bar: Martini,
  restaurant: Utensils,
  sunbeds: BedDouble,
  umbrellas: Umbrella,
  parking: ParkingSquare,
  disabled_access: Accessibility,
  pets_allowed: Dog,
  water_sports: Waves,
};

// Difficulty Badge Component
function DifficultyBadge({ difficulty, darkMode }: { difficulty: string; darkMode: boolean }) {
  const { t } = useTranslation();
  const colors = {
    easy: darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700',
    moderate: darkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700',
    hard: darkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700',
    expert: darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[difficulty as keyof typeof colors] || colors.moderate}`}>
      {t(`guest.explore.difficulty.${difficulty}`, difficulty)}
    </span>
  );
}

// Event Date Badge Component
function EventDateBadge({ recommendation, darkMode }: { recommendation: LocalRecommendation; darkMode: boolean }) {
  const { t, i18n } = useTranslation();

  if (!recommendation.event_date && !recommendation.is_recurring) return null;

  // event_date is a calendar day — parsed as LOCAL (new Date('YYYY-MM-DD') is
  // UTC and showed the previous day west of UTC), in the guest's own language
  // (it was only ever hr-HR or en-US).
  const formatDate = (dateStr: string) =>
    formatGuestDate(dateStr, i18n.language, { weekday: 'short', day: 'numeric', month: 'short' });

  const getDayName = (dayNum: number) => weekdayName(dayNum, i18n.language);

  return (
    <div className={`flex items-center gap-2 mt-2 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
      <Calendar className="w-4 h-4" />
      <span className="text-sm font-medium">
        {recommendation.is_recurring && recommendation.recurrence_day !== null
          ? `${t('guest.explore.every')} ${getDayName(recommendation.recurrence_day)}`
          : recommendation.event_date
          ? formatDate(recommendation.event_date)
          : null
        }
      </span>
      {recommendation.event_time && (
        <>
          <Clock className="w-4 h-4 ml-2" />
          <span className="text-sm">{recommendation.event_time}</span>
        </>
      )}
    </div>
  );
}

// Beach Info Section
function BeachInfoSection({ recommendation, darkMode }: { recommendation: LocalRecommendation; darkMode: boolean }) {
  const { t } = useTranslation();

  if (recommendation.category !== 'beach') return null;

  const beachTypeLabels: Record<string, string> = {
    sandy: t('guest.explore.beachType.sandy', 'Pješčana'),
    pebble: t('guest.explore.beachType.pebble', 'Šljunčana'),
    rocky: t('guest.explore.beachType.rocky', 'Stjenovita'),
    concrete: t('guest.explore.beachType.concrete', 'Betonska'),
    mixed: t('guest.explore.beachType.mixed', 'Mješovita'),
  };

  return (
    <div className="mt-3 space-y-2">
      {/* Beach Type */}
      {recommendation.beach_type && (
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm ${
          darkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'
        }`}>
          <Waves className="w-4 h-4" />
          {beachTypeLabels[recommendation.beach_type] || recommendation.beach_type}
        </div>
      )}

      {/* Beach Facilities */}
      {recommendation.beach_facilities && recommendation.beach_facilities.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {recommendation.beach_facilities.map((facility) => {
            const FacilityIcon = BEACH_FACILITY_ICONS[facility];
            return (
              <span
                key={facility}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs ${
                  darkMode ? 'bg-slate-700/50 text-slate-300' : 'bg-gray-100 text-gray-600'
                }`}
                title={t(`guest.explore.beachFacilities.${facility}`, facility)}
              >
                {FacilityIcon
                  ? <FacilityIcon className={`w-4 h-4 ${darkMode ? 'text-sky-400' : 'text-sky-500'}`} />
                  : <span className={darkMode ? 'text-sky-400' : 'text-sky-500'}>•</span>}
                {t(`guest.explore.beachFacilities.${facility}`, facility)}
              </span>
            );
          })}
        </div>
      )}

      {/* Suitable For */}
      {recommendation.suitable_for && recommendation.suitable_for.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {recommendation.suitable_for.includes('families') && (
            <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
              <Baby className="w-3 h-3" /> {t('guest.explore.suitableFor.families', 'Za obitelji')}
            </span>
          )}
          {recommendation.suitable_for.includes('pets') && (
            <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              <Dog className="w-3 h-3" /> {t('guest.explore.suitableFor.pets', 'Kućni ljubimci')}
            </span>
          )}
          {recommendation.suitable_for.includes('elderly') && (
            <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              <Accessibility className="w-3 h-3" /> {t('guest.explore.suitableFor.elderly', 'Prilagođeno')}
            </span>
          )}
        </div>
      )}

      {/* Water Info */}
      {(recommendation.water_depth || recommendation.entry_type) && (
        <div className={`flex items-center gap-3 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
          {recommendation.entry_type && (
            <span className="flex items-center gap-1">
              <Waves className="w-3 h-3" />
              {t(`guest.explore.entryType.${recommendation.entry_type}`, recommendation.entry_type)}
            </span>
          )}
          {recommendation.water_depth && (
            <span>• {recommendation.water_depth}</span>
          )}
        </div>
      )}
    </div>
  );
}

// Activity Info Section
function ActivityInfoSection({ recommendation, darkMode }: { recommendation: LocalRecommendation; darkMode: boolean }) {
  const { t } = useTranslation();

  const isActivity = ['hiking', 'cycling', 'diving', 'water_sports', 'excursion'].includes(recommendation.category);
  if (!isActivity) return null;

  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        {/* Difficulty */}
        {recommendation.difficulty && (
          <DifficultyBadge difficulty={recommendation.difficulty} darkMode={darkMode} />
        )}

        {/* Duration */}
        {recommendation.duration_hours && (
          <span className={`flex items-center gap-1 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            <Timer className="w-4 h-4" />
            {recommendation.duration_hours}h
          </span>
        )}

        {/* Price */}
        {recommendation.price_info && (
          <span className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
            {recommendation.price_info}
          </span>
        )}
      </div>

      {/* GPX Download */}
      {recommendation.gpx_url && (
        <a
          href={recommendation.gpx_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            darkMode
              ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
              : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
          }`}
        >
          <Route className="w-4 h-4" />
          {t('guest.explore.downloadGpx', 'Preuzmi GPX rutu')}
          <Download className="w-3 h-3" />
        </a>
      )}

      {/* Schedule URL */}
      {recommendation.schedule_url && (
        <a
          href={recommendation.schedule_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 text-sm ${
            darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <Calendar className="w-4 h-4" />
          {t('guest.explore.viewSchedule', 'Vidi raspored')}
          <ExternalLink className="w-3 h-3" />
        </a>
      )}

      {/* How to Get There */}
      {recommendation.how_to_get_there && (
        <div className={`mt-2 p-3 rounded-xl ${darkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-1">
            <Navigation className={`w-4 h-4 ${darkMode ? 'text-sky-400' : 'text-sky-500'}`} />
            <span className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              {t('guest.explore.howToGetThere', 'Kako doći')}
            </span>
          </div>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            {recommendation.how_to_get_there}
          </p>
        </div>
      )}
    </div>
  );
}

// Insider Tip Component
function InsiderTip({ tip, darkMode }: { tip: string; darkMode: boolean }) {
  const { t } = useTranslation();

  return (
    <div className={`mt-3 p-3 rounded-xl ${darkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb className={`w-4 h-4 ${darkMode ? 'text-amber-300' : 'text-amber-600'}`} />
        <span className={`text-sm font-medium ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
          {t('guest.explore.insiderTip', 'Savjet')}
        </span>
      </div>
      <p className={`text-sm ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>
        {tip}
      </p>
    </div>
  );
}

// Gradient maps for category groups - gives each group a unique color feel
const GROUP_GRADIENTS: Record<string, { light: string; dark: string; glow: string }> = {
  food: {
    light: 'from-orange-400 via-amber-400 to-yellow-400',
    dark: 'from-orange-500 via-amber-500 to-yellow-500',
    glow: 'shadow-orange-400/30',
  },
  nature: {
    light: 'from-emerald-400 via-green-400 to-teal-400',
    dark: 'from-emerald-500 via-green-500 to-teal-500',
    glow: 'shadow-emerald-400/30',
  },
  activities: {
    light: 'from-blue-400 via-cyan-400 to-sky-400',
    dark: 'from-blue-500 via-cyan-500 to-sky-500',
    glow: 'shadow-blue-400/30',
  },
  transport: {
    light: 'from-yellow-400 via-amber-400 to-orange-400',
    dark: 'from-yellow-500 via-amber-500 to-orange-500',
    glow: 'shadow-yellow-400/30',
  },
  services: {
    light: 'from-red-400 via-rose-400 to-pink-400',
    dark: 'from-red-500 via-rose-500 to-pink-500',
    glow: 'shadow-red-400/30',
  },
  events: {
    light: 'from-purple-400 via-violet-400 to-fuchsia-400',
    dark: 'from-purple-500 via-violet-500 to-fuchsia-500',
    glow: 'shadow-purple-400/30',
  },
  other: {
    light: 'from-slate-400 via-gray-400 to-zinc-400',
    dark: 'from-slate-500 via-gray-500 to-zinc-500',
    glow: 'shadow-slate-400/30',
  },
};

// Category Card for navigation - 2-column grid style with gradients
function CategoryCard({
  category,
  count,
  onClick,
  darkMode,
}: {
  category: typeof RECOMMENDATION_CATEGORIES[0];
  count: number;
  onClick: () => void;
  darkMode: boolean;
}) {
  const { t } = useTranslation();
  const gradient = GROUP_GRADIENTS[category.group] || GROUP_GRADIENTS.other;

  return (
    <button
      onClick={onClick}
      className={`group relative flex flex-col items-center gap-1.5 p-4 pb-3 rounded-2xl transition-all duration-200 active:scale-[0.95] ${
        darkMode
          ? 'bg-slate-800/70 hover:bg-slate-750/80 border border-slate-700/50'
          : 'bg-white hover:bg-gray-50/80 border border-gray-100 shadow-md hover:shadow-lg'
      }`}
    >
      {/* Count badge */}
      <span className={`absolute top-2 right-2 min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold ${
        darkMode
          ? 'bg-slate-700 text-slate-300'
          : 'bg-gray-100 text-gray-500'
      }`}>
        {count}
      </span>

      {/* Gradient icon with glow effect */}
      <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${
        darkMode ? gradient.dark : gradient.light
      } flex items-center justify-center shadow-lg ${gradient.glow} group-hover:scale-110 transition-transform duration-200`}>
        <RecommendationIcon category={category.value} className="w-6 h-6 text-white" />
        {/* Subtle shine overlay */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-white/25 via-transparent to-transparent" />
      </div>

      {/* Category name */}
      <p className={`text-xs font-semibold text-center leading-tight line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
        {t(`localGuide.categories.${category.value}`, category.label)}
      </p>
    </button>
  );
}

// Category Group Header — strong text title (no icon badge; category tiles below carry the iconography).
function CategoryGroupHeader({ group, darkMode }: { group: CategoryGroup; darkMode: boolean }) {
  const { t } = useTranslation();
  const groupInfo = CATEGORY_GROUPS[group];

  return (
    <div className="flex items-center gap-2.5 mb-3">
      <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {t(`guest.explore.groups.${group}`, groupInfo.label)}
      </h3>
      <div className={`flex-1 h-px ${darkMode ? 'bg-slate-700/50' : 'bg-gray-200'}`} />
    </div>
  );
}

// Recommendation Card
function RecommendationCard({
  recommendation: rawRecommendation,
  darkMode,
  onNavigate,
}: {
  recommendation: LocalRecommendation;
  darkMode: boolean;
  onNavigate: (lat: number, lng: number) => void;
}) {
  const { t } = useTranslation();
  // Host-written texts in the guest's language (name stays as written).
  const { translations: tr } = useTranslatedRecommendation(rawRecommendation);
  const recommendation: LocalRecommendation = {
    ...rawRecommendation,
    description: tr.description?.translated || rawRecommendation.description,
    how_to_get_there: tr.how_to_get_there?.translated || rawRecommendation.how_to_get_there,
    insider_tip: tr.insider_tip?.translated || rawRecommendation.insider_tip,
    personal_note: tr.personal_note?.translated || rawRecommendation.personal_note,
  };
  const [showMap, setShowMap] = useState(false);
  const categoryInfo = getCategoryInfo(recommendation.category);
  const hasCoordinates = recommendation.latitude && recommendation.longitude;

  const handleNavigate = () => {
    if (recommendation.google_maps_url) {
      window.open(recommendation.google_maps_url, '_blank');
    } else if (hasCoordinates) {
      onNavigate(recommendation.latitude!, recommendation.longitude!);
    }
  };

  const handleCall = () => {
    if (recommendation.phone) {
      window.location.href = `tel:${recommendation.phone}`;
    }
  };

  // Check if this is an event category
  const isEvent = ['event', 'festival', 'concert'].includes(recommendation.category);

  return (
    <div
      className={`rounded-2xl overflow-hidden ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-white border border-gray-100/60 shadow-lg shadow-gray-200/80'
      }`}
    >
      {/* Image if available */}
      {recommendation.image_url && (
        <div className="relative h-40 overflow-hidden">
          <img
            src={recommendation.image_url}
            alt={recommendation.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-3 left-3 right-3">
            <h4 className="font-bold text-lg text-white truncate">{recommendation.name}</h4>
          </div>
        </div>
      )}

      <div className="p-4">
        {/* Header (only if no image) */}
        {!recommendation.image_url && (
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl ${categoryInfo.color} flex items-center justify-center text-lg font-bold text-white shrink-0`}>
              {recommendation.name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {recommendation.name}
                </h4>
                {recommendation.is_personal_recommendation && (
                  <Heart className="w-5 h-5 text-pink-500 fill-pink-500 shrink-0" />
                )}
              </div>
              {recommendation.distance_km && (
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  {recommendation.distance_km} km {t('guest.explore.away', 'daleko')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Event Date */}
        {isEvent && <EventDateBadge recommendation={recommendation} darkMode={darkMode} />}

        {/* Rating */}
        {recommendation.landlord_rating && (
          <div className="flex items-center gap-1 mt-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-4 h-4 ${
                  star <= recommendation.landlord_rating! ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
                }`}
              />
            ))}
            <span className={`text-sm ml-2 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              {t('guest.explore.hostRating', 'Ocjena domaćina')}
            </span>
          </div>
        )}

        {/* Description */}
        {recommendation.description && (
          <p className={`text-sm mt-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
            {recommendation.description}
          </p>
        )}

        {/* Beach-specific info */}
        <BeachInfoSection recommendation={recommendation} darkMode={darkMode} />

        {/* Activity-specific info */}
        <ActivityInfoSection recommendation={recommendation} darkMode={darkMode} />

        {/* Insider Tip */}
        {recommendation.insider_tip && (
          <InsiderTip tip={recommendation.insider_tip} darkMode={darkMode} />
        )}

        {/* Personal Note */}
        {recommendation.personal_note && (
          <div className={`mt-3 p-3 rounded-xl ${darkMode ? 'bg-pink-500/10' : 'bg-pink-50'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-4 h-4 text-pink-500" />
              <span className={`text-sm font-medium ${darkMode ? 'text-pink-300' : 'text-pink-600'}`}>
                {t('guest.explore.hostSays', 'Domaćin kaže')}
              </span>
            </div>
            <p className={`text-sm italic ${darkMode ? 'text-pink-200' : 'text-pink-700'}`}>
              "{recommendation.personal_note}"
            </p>
          </div>
        )}

        {/* Address with map toggle */}
        {recommendation.address && (
          <div className="mt-3">
            <button
              onClick={() => hasCoordinates && setShowMap(!showMap)}
              className={`w-full flex items-center justify-between gap-2 text-left ${
                hasCoordinates ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <p className={`text-sm flex items-center gap-2 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                <MapPin className={`w-4 h-4 shrink-0 ${darkMode ? 'text-sky-400' : 'text-sky-500'}`} />
                <span className="truncate">{recommendation.address}</span>
              </p>
              {hasCoordinates && (
                <span className={`shrink-0 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                  {showMap ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          {(recommendation.google_maps_url || hasCoordinates) && (
            <button
              onClick={handleNavigate}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium transition-colors ${
                darkMode
                  ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
            >
              <Navigation className="w-4 h-4" />
              {t('guest.explore.navigate', 'Navigiraj')}
            </button>
          )}
          {recommendation.phone && (
            <button
              onClick={handleCall}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                darkMode
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  : 'bg-green-50 text-green-600 hover:bg-green-100'
              }`}
            >
              <Phone className="w-4 h-4" />
            </button>
          )}
          {recommendation.website && (
            <button
              onClick={() => window.open(recommendation.website!, '_blank')}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expandable Map */}
      {showMap && hasCoordinates && (
        <div className={`border-t ${darkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
          <MapEmbed
            latitude={recommendation.latitude!}
            longitude={recommendation.longitude!}
            title={recommendation.name}
            zoom={15}
            height="160px"
            darkMode={darkMode}
            showOpenButton={true}
            className="rounded-none"
          />
        </div>
      )}
    </div>
  );
}

// Category Detail View
function CategoryDetailView({
  category,
  recommendations,
  darkMode,
  onBack,
  onNavigate,
}: {
  category: RecommendationCategory;
  recommendations: LocalRecommendation[];
  darkMode: boolean;
  onBack: () => void;
  onNavigate: (lat: number, lng: number) => void;
}) {
  const { t } = useTranslation();
  const categoryInfo = getCategoryInfo(category);

  return (
    <div className="space-y-4">
      {/* Back Button */}
      <button
        onClick={onBack}
        className={`flex items-center gap-2 text-sm font-medium ${
          darkMode ? 'text-blue-400' : 'text-blue-600'
        }`}
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        {t('guest.explore.backToCategories', 'Natrag na kategorije')}
      </button>

      {/* Category Header */}
      <div className={`flex items-center gap-3 p-4 rounded-2xl ${
        darkMode ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5' : 'bg-white shadow-lg'
      }`}>
        <div className={`w-14 h-14 rounded-xl ${categoryInfo.color} flex items-center justify-center`}>
          <RecommendationIcon category={category} className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {t(`localGuide.categories.${category}`, categoryInfo.label)}
          </h2>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            {recommendations.length} {t('guest.explore.placesFound', 'pronađeno')}
          </p>
        </div>
      </div>

      {/* Recommendations List */}
      <div className="space-y-4">
        {recommendations.map((rec) => (
          <RecommendationCard
            key={rec.id}
            recommendation={rec}
            darkMode={darkMode}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

// Main Component
export function GuestExploreTab({
  recommendations,
  darkMode,
}: GuestExploreTabProps) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<RecommendationCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Group recommendations by category
  const groupedRecommendations = useMemo(() => {
    return recommendations.reduce((acc, rec) => {
      if (!acc[rec.category]) {
        acc[rec.category] = [];
      }
      acc[rec.category].push(rec);
      return acc;
    }, {} as Record<RecommendationCategory, LocalRecommendation[]>);
  }, [recommendations]);

  // Filter categories that have recommendations and organize by group
  const categoriesByGroup = useMemo(() => {
    const result: Record<CategoryGroup, typeof RECOMMENDATION_CATEGORIES> = {
      food: [],
      nature: [],
      activities: [],
      transport: [],
      services: [],
      events: [],
      other: [],
    };

    RECOMMENDATION_CATEGORIES.forEach((cat) => {
      if (groupedRecommendations[cat.value]?.length > 0) {
        result[cat.group].push(cat);
      }
    });

    return result;
  }, [groupedRecommendations]);

  // Handle navigation
  const handleNavigate = (lat: number, lng: number) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  };

  // Filter by search
  const filteredRecommendations = useMemo(() => {
    if (!searchQuery) return [];
    return recommendations.filter(
      (rec) =>
        rec.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rec.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, recommendations]);

  if (selectedCategory) {
    return (
      <CategoryDetailView
        category={selectedCategory}
        recommendations={groupedRecommendations[selectedCategory] || []}
        darkMode={darkMode}
        onBack={() => setSelectedCategory(null)}
        onNavigate={handleNavigate}
      />
    );
  }

  // Check if there are any recommendations
  const hasAnyRecommendations = Object.values(categoriesByGroup).some(cats => cats.length > 0);

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
          darkMode ? 'text-slate-400' : 'text-gray-400'
        }`} />
        <input
          type="text"
          placeholder={t('guest.explore.searchPlaceholder', 'Pretraži lokalne preporuke...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full pl-12 pr-4 py-3 rounded-xl ${
            darkMode
              ? 'bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-400'
              : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-400'
          }`}
        />
      </div>

      {/* Search Results */}
      {searchQuery && (
        <div className="space-y-4">
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('guest.explore.searchResults', 'Rezultati pretrage')} ({filteredRecommendations.length})
          </h3>
          {filteredRecommendations.length > 0 ? (
            filteredRecommendations.map((rec) => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                darkMode={darkMode}
                onNavigate={handleNavigate}
              />
            ))
          ) : (
            <p className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('guest.explore.noResults', 'Nema rezultata')}
            </p>
          )}
        </div>
      )}

      {/* Categories grouped */}
      {!searchQuery && hasAnyRecommendations && (
        <div className="space-y-6">
          {(Object.entries(categoriesByGroup) as [CategoryGroup, typeof RECOMMENDATION_CATEGORIES][]).map(
            ([group, categories]) => {
              if (categories.length === 0) return null;

              return (
                <div key={group}>
                  <CategoryGroupHeader group={group} darkMode={darkMode} />
                  <div className="grid grid-cols-3 gap-2.5">
                    {categories.map((cat) => (
                      <CategoryCard
                        key={cat.value}
                        category={cat}
                        count={groupedRecommendations[cat.value]?.length || 0}
                        onClick={() => setSelectedCategory(cat.value)}
                        darkMode={darkMode}
                      />
                    ))}
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}

      {/* Empty State */}
      {!searchQuery && !hasAnyRecommendations && (
        <div className={`text-center py-12 rounded-2xl ${
          darkMode ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5' : 'bg-white shadow-lg'
        }`}>
          <MapPin className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('guest.explore.noRecommendations', 'Nema preporuka')}
          </h3>
          <p className={`${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            {t('guest.explore.noRecommendationsDescription', 'Domaćin još nije dodao preporuke za ovu lokaciju.')}
          </p>
        </div>
      )}
    </div>
  );
}

export default GuestExploreTab;
